// Taslak anket düzenleme kipinde soru listesinin sunucuya yazılması (final inceleme I6). Eskiden düzenleme kipinde yalnız
// başlık/açıklama kaydediliyordu (SurveyDataAccess.Update soruya dokunmaz) — "Şablondan Başlat" sonrası kırılım sorusu
// seçeneklerinde yapılan değişiklikler sessizce kayboluyordu.
//
// Kural: yüklenen liste ile güncel liste karşılaştırılır.
//  • Sunucu kimlikli (yüklenmiş) soru değiştiyse updateQuestion — Code/DimensionId/IsHeadline/IsBreakdown/uygunluk sunucuda
//    korunur (UpdateQuestion onlara dokunmaz); rapor ayarları soru kimliğini tuttuğu için kırılım ayarı da korunur.
//  • Geçici (sayısal) kimlikli soru addQuestion ile eklenir.
//  • Yüklenmiş ama listede olmayan soru deleteQuestion ile silinir.
//  • Sonunda reorderQuestions son sırayı yazar.

/** Yüklenmiş (sunucudan gelen) soru kimliği mi — yeni eklenenler Date.now() tabanlı sayısal geçici kimlik taşır. */
export const isServerId = (id) => typeof id === 'string' && id.length > 0;

/**
 * @param original yüklendiği andaki soru listesi
 * @param current kaydedilecek soru listesi (sıra önemli)
 * @param toPayload soru → istek gövdesi (aynı dönüşüm iki listeye de uygulanır; yalnız gerçek fark güncelleme doğurur)
 */
export function planQuestionEdits(original, current, toPayload) {
  const originalById = new Map(original.filter((q) => isServerId(q.id)).map((q) => [q.id, q]));
  const currentIds = new Set(current.map((q) => q.id));
  const updates = [];
  const adds = [];
  current.forEach((q, index) => {
    const payload = toPayload(q);
    const before = originalById.get(q.id);
    if (before) {
      if (JSON.stringify(toPayload(before)) !== JSON.stringify(payload)) updates.push({ id: q.id, payload });
    } else {
      adds.push({ index, payload });
    }
  });
  const deletes = [...originalById.keys()].filter((id) => !currentIds.has(id));
  return { updates, adds, deletes };
}

/**
 * Planı uygular; dönen dizi güncel listenin sıradaki sunucu kimlikleridir (eklenenler yeni kimlikleriyle).
 * Servis çağrıları reddederse hata yukarı taşınır (toast interceptor'da). Yarıda kalırsa (yeniden inceleme N3) hataya
 * `applied = { deletedIds, updatedIds, added: [{ tempId, id }] }` eklenir — çağıran reconcileAfterPartialSave ile
 * yüklü listeyi sunucu durumuna çeker; yeniden denemede silinen soru tekrar silinmez (404), eklenen tekrar eklenmez.
 */
export async function persistQuestionEdits(surveyId, original, current, toPayload, api) {
  const plan = planQuestionEdits(original, current, toPayload);
  const applied = { deletedIds: [], updatedIds: [], added: [] };
  try {
    for (const id of plan.deletes) {
      await api.deleteQuestion(surveyId, id);
      applied.deletedIds.push(id);
    }
    for (const u of plan.updates) {
      await api.updateQuestion(surveyId, u.id, u.payload);
      applied.updatedIds.push(u.id);
    }
    const ids = current.map((q) => q.id);
    for (const a of plan.adds) {
      const res = await api.addQuestion(surveyId, a.payload);
      const newId = res?.data?.data?.id;
      if (!newId) throw new Error('survey.questionNotSaved');
      applied.added.push({ tempId: current[a.index].id, id: newId });
      ids[a.index] = newId;
    }
    await api.reorderQuestions(surveyId, { items: ids.map((id, orderIndex) => ({ id, orderIndex })) });
    return ids;
  } catch (err) {
    const error = err instanceof Error || (err && typeof err === 'object') ? err : new Error(String(err));
    error.applied = applied;
    throw error;
  }
}

/**
 * Yarıda kalan kaydın ardından (yeniden inceleme N3): `original` sunucudaki duruma çekilir — uygulanan silmeler çıkar,
 * uygulanan güncellemeler güncel hâliyle, uygulanan eklemeler yeni sunucu kimlikleriyle girer. `idMap` geçici → sunucu
 * kimliği; çağıran güncel listedeki geçici kimlikleri bununla değiştirir. Uygulanmamış değişiklikler yeniden denemede
 * yeniden planlanır.
 */
export function reconcileAfterPartialSave(original, current, applied) {
  const idMap = new Map((applied?.added || []).map((a) => [a.tempId, a.id]));
  const updated = new Set(applied?.updatedIds || []);
  const deleted = new Set(applied?.deletedIds || []);
  const currentById = new Map(current.map((q) => [q.id, q]));
  const nextOriginal = [
    ...original
      .filter((q) => !deleted.has(q.id))
      .map((q) => (updated.has(q.id) && currentById.has(q.id) ? currentById.get(q.id) : q)),
    ...current.filter((q) => idMap.has(q.id)).map((q) => ({ ...q, id: idMap.get(q.id) })),
  ];
  return { idMap, original: nextOriginal };
}

/**
 * Yeniden inceleme N3 — create yanıtında `questionIds` eksik ya da sayısı tutmuyorsa (yeni SPA eski API'ye denk geldi)
 * taslağın soruları `getSurvey` ile okunur. GET interceptor'da reddetmez; sinyal `res.data.status`. Sunucu soruları create
 * isteğinin sırasıyla (OrderIndex) döner, bu yüzden `snapshot` (create'e gönderilen geçici kimlikli liste) sırayla eşlenir;
 * `current` içindeki o geçici kimlikler sunucu kimlikleriyle değişir (arada yapılan düzenlemeler korunur, fark kaydına girer).
 * Okunamazsa ya da soru sayısı tutmazsa `null` döner — çağıran hiçbir şey yazmaz (sorular ikinci kez eklenmez).
 * @returns {{ original: object[], questions: object[] } | null}
 */
export async function syncCreatedQuestions(surveyId, snapshot, current, getSurvey, fromServer) {
  let res;
  try {
    res = await getSurvey(surveyId);
  } catch {
    return null;
  }
  const survey = res?.data?.status ? res.data.data : null;
  if (!survey || !Array.isArray(survey.questions) || survey.questions.length !== snapshot.length) return null;
  const original = survey.questions.map(fromServer);
  if (original.some((q) => !isServerId(q.id))) return null;
  const idMap = new Map(snapshot.map((q, i) => [q.id, original[i].id]));
  return {
    original,
    questions: current.map((q) => (idMap.has(q.id) ? { ...q, id: idMap.get(q.id) } : q)),
  };
}
